import React, { forwardRef, useRef } from 'react';

import { BaseTextInputProps } from '../shared-types';
import BaseInput from '../BaseInput/BaseInput';
import InputControl from '../InputControl/InputControl';
import { noop, useForkRef, useInputComponentIdAndLabelData } from '../utils';

export type Props = BaseTextInputProps & {
  adornmentLeft?: React.ReactNode;
  adornmentRight?: React.ReactNode;
};

const TextInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    label,
    error,
    showRequiredAsterisk,
    hint,
    eventId,
    type = 'text',
    onChange = noop,
    onHintToggle = noop,
    'aria-label': ariaLabel,
    styleInputContainer = {},
    adornmentLeft,
    adornmentRight,
    ...inputProps
  } = props;

  const inputRef = useRef<HTMLInputElement>();
  const handleRef: React.Ref<HTMLInputElement> = useForkRef(ref, inputRef);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event.target?.value || '');
  };

  const {
    containerId,
    labelId,
    'aria-labelledby': ariaLabelledby,
    hintId,
  } = useInputComponentIdAndLabelData('TextInput', label, ariaLabel);

  return (
    <InputControl
      id={containerId}
      label={label}
      labelId={labelId}
      errorMessage={error}
      showRequiredAsterisk={showRequiredAsterisk}
      hint={hint}
      hintId={hintId}
      onHintToggle={onHintToggle}
      eventId={eventId}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sx={styleInputContainer as any}
    >
      <BaseInput
        {...inputProps}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        type={type}
        label={label}
        error={!!error}
        componentName="TextInput"
        onChange={handleChange}
        adornmentLeft={adornmentLeft}
        adornmentRight={adornmentRight}
        ref={handleRef}
      />
    </InputControl>
  );
});

export default TextInput;
